import type { OrganizerGroup } from '@pulsify/shared/dto'
import { Copy, RefreshCw } from 'lucide-react'
import { type FormEvent, useEffect, useId, useState } from 'react'
import { toast } from 'sonner'
import { useEvents } from '~/entities/event/model/use-events'
import {
  useCreateOrganizerGroup,
  useOrganizerGroupMembers,
  useRemoveOrganizerGroupMember,
  useResetOrganizerGroupInvite,
  useSendOrganizerGroupMessage,
  useUpdateOrganizerGroup,
} from '~/entities/organizer-group/model/use-organizer-groups'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { copyToClipboard } from '~/shared/lib/share'
import { Avatar, AvatarFallback } from '~/shared/ui/avatar'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/ui/dialog'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Switch } from '~/shared/ui/switch'
import { Textarea } from '~/shared/ui/textarea'

const fail = (error: unknown) => toast.error(getErrorMessageFromError(error))

function SwitchRow({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string
  label: string
  hint: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <Label htmlFor={id}>{label}</Label>
        <p className="text-muted-foreground text-xs">{hint}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  )
}

/** Starts a group, or changes one with its invite link when \`group\` is given. */
export function GroupFormDialog({
  open,
  onOpenChange,
  businessId,
  profileId,
  showListed,
  group,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  profileId: string
  /** Only organizer pages show groups in the app; a place's group goes by its link. */
  showListed: boolean
  group: OrganizerGroup | null
  onCreated?: (group: OrganizerGroup) => void
}) {
  const id = useId()
  const create = useCreateOrganizerGroup(businessId)
  const update = useUpdateOrganizerGroup(businessId, group?.id ?? '')
  const reset = useResetOrganizerGroupInvite(businessId, group?.id ?? '')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [onlyBusinessWrites, setOnlyBusinessWrites] = useState(false)
  const [listed, setListed] = useState(true)

  useEffect(() => {
    if (!open) return
    setTitle(group?.title ?? '')
    setDescription(group?.description ?? '')
    setOnlyBusinessWrites(group?.onlyBusinessWrites ?? false)
    setListed(group?.listed ?? true)
  }, [open, group])

  const saving = create.isPending || update.isPending

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const fields = {
      title,
      description: description.trim() || null,
      onlyBusinessWrites,
      listed,
    }
    if (group) {
      update.mutate(fields, {
        onSuccess: () => {
          toast.success(m.groupsSaved())
          onOpenChange(false)
        },
        onError: fail,
      })
      return
    }
    create.mutate(
      { ...fields, hostProfileId: profileId },
      {
        onSuccess: (created) => {
          toast.success(m.groupsCreated())
          onOpenChange(false)
          onCreated?.(created)
        },
        onError: fail,
      },
    )
  }

  const copyLink = async () => {
    if (group && (await copyToClipboard(group.inviteUrl))) toast.success(m.groupsLinkCopied())
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{group ? m.groupsSettings() : m.groupsNew()}</DialogTitle>
          <DialogDescription>{m.groupsFormHint()}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-title`}>{m.groupsFieldTitle()}</Label>
            <Input
              id={`${id}-title`}
              value={title}
              required
              minLength={2}
              maxLength={80}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-description`}>{m.groupsFieldDescription()}</Label>
            <Textarea
              id={`${id}-description`}
              value={description}
              maxLength={500}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          <SwitchRow
            id={`${id}-only`}
            label={m.groupsOnlyBusiness()}
            hint={m.groupsOnlyBusinessHint()}
            checked={onlyBusinessWrites}
            onChange={setOnlyBusinessWrites}
          />
          {showListed ? (
            <SwitchRow
              id={`${id}-listed`}
              label={m.groupsListed()}
              hint={m.groupsListedHint()}
              checked={listed}
              onChange={setListed}
            />
          ) : null}
          {group ? (
            <div className="space-y-1.5">
              <Label htmlFor={`${id}-link`}>{m.groupsInviteLink()}</Label>
              <div className="flex gap-2">
                <Input id={`${id}-link`} value={group.inviteUrl} readOnly />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label={m.groupsLinkCopy()}
                  onClick={copyLink}
                >
                  <Copy />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label={m.groupsLinkReset()}
                  disabled={reset.isPending}
                  onClick={() =>
                    reset.mutate(undefined, {
                      onSuccess: () => toast.success(m.groupsLinkResetDone()),
                      onError: fail,
                    })
                  }
                >
                  <RefreshCw />
                </Button>
              </div>
              <p className="text-muted-foreground text-xs">{m.groupsLinkResetHint()}</p>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {m.cancel()}
            </Button>
            <Button type="submit" disabled={saving}>
              {group ? m.groupsSave() : m.groupsCreate()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function GroupMembersDialog({
  open,
  onOpenChange,
  businessId,
  groupId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  groupId: string
}) {
  const members = useOrganizerGroupMembers(businessId, groupId, open)
  const remove = useRemoveOrganizerGroupMember(businessId, groupId)
  const [confirming, setConfirming] = useState<string | null>(null)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{m.groupsMembers()}</DialogTitle>
          <DialogDescription>{m.groupsMembersHint()}</DialogDescription>
        </DialogHeader>
        {members.data?.length === 0 ? (
          <p className="text-muted-foreground text-sm">{m.groupsMembersEmpty()}</p>
        ) : (
          <ul className="max-h-96 divide-y divide-border overflow-y-auto">
            {members.data?.map((member) => {
              const name = member.displayName ?? m.groupsMemberGone()
              return (
                <li key={member.id} className="flex items-center gap-3 py-2">
                  <Avatar className="size-8">
                    {member.avatarUrl ? (
                      <img src={member.avatarUrl} alt="" className="size-full object-cover" />
                    ) : (
                      <AvatarFallback>{name.slice(0, 1)}</AvatarFallback>
                    )}
                  </Avatar>
                  <span className="min-w-0 flex-1 truncate text-sm">{name}</span>
                  {confirming === member.id ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      disabled={remove.isPending}
                      onClick={() =>
                        remove.mutate(member.id, {
                          onSuccess: () => {
                            setConfirming(null)
                            toast.success(m.groupsMemberRemoved({ name }))
                          },
                          onError: fail,
                        })
                      }
                    >
                      {m.groupsMemberRemoveConfirm()}
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setConfirming(member.id)}>
                      {m.groupsMemberRemove()}
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Posts one of the profile's coming events into the group, with its ticket button in the app. */
export function ShareEventDialog({
  open,
  onOpenChange,
  businessId,
  groupId,
  profileId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  groupId: string
  profileId: string
}) {
  const coming = useEvents(businessId, 'upcoming', profileId)
  const send = useSendOrganizerGroupMessage(businessId, groupId)
  const published = (coming.events ?? []).filter((event) => event.status === 'Published')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{m.groupsShareEvent()}</DialogTitle>
          <DialogDescription>{m.groupsShareEventHint()}</DialogDescription>
        </DialogHeader>
        {published.length === 0 ? (
          <p className="text-muted-foreground text-sm">{m.groupsShareEventEmpty()}</p>
        ) : (
          <ul className="max-h-96 divide-y divide-border overflow-y-auto">
            {published.map((event) => (
              <li key={event.id} className="flex items-center justify-between gap-3 py-2">
                <span className="min-w-0 truncate text-sm">{event.title}</span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={send.isPending}
                  onClick={() =>
                    send.mutate(
                      { kind: 'event_share', eventId: event.id },
                      { onSuccess: () => onOpenChange(false), onError: fail },
                    )
                  }
                >
                  {m.groupsShareEventSend()}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}
